import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  evaluationReportsApi, 
  GenerateEvaluationReportDto, 
  ApproveReportDto, 
  AddBidCommitteeMembersDto,
  TenderEvaluationReport 
} from '../../../api/evaluationReports';
import { notifications } from '@mantine/notifications';

export const useCompleteEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenderId: string) => evaluationReportsApi.completeEvaluation(tenderId),
    onSuccess: (_data, tenderId) => {
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tenders'] });
      notifications.show({
        title: 'Success',
        message: 'Evaluation completed successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to complete evaluation',
        color: 'red',
      });
    },
  });
};

export const useGenerateEvaluationReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: GenerateEvaluationReportDto }) =>
      evaluationReportsApi.generateEvaluationReport(tenderId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport', variables.tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', variables.tenderId] });
      notifications.show({
        title: 'Success',
        message: 'Evaluation report generated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to generate evaluation report',
        color: 'red',
      });
    },
  });
};

export const useEvaluationReport = (tenderId: string) => {
  console.log('useEvaluationReport called with tenderId:', tenderId, 'enabled:', !!tenderId);
  
  const query = useQuery({
    queryKey: ['evaluationReport', tenderId],
    queryFn: async () => {
      console.log('useEvaluationReport queryFn executing for tenderId:', tenderId);
      const report = await evaluationReportsApi.getEvaluationReport(tenderId);
      console.log('useEvaluationReport - report received:', report);
      console.log('useEvaluationReport - report type:', typeof report);
      console.log('useEvaluationReport - report is null?', report === null);
      console.log('useEvaluationReport - report is undefined?', report === undefined);
      
      // Return the report as-is, even if null (React Query will handle it)
      return report;
    },
    enabled: !!tenderId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: false, // Don't retry on 404
    refetchOnWindowFocus: false,
  });
  
  console.log('useEvaluationReport query state:', {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isError: query.isError,
    status: query.status,
  });
  
  return query;
};

export const useCheckReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: ApproveReportDto }) =>
      evaluationReportsApi.checkReport(reportId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReportApprovals'] });
      notifications.show({
        title: 'Success',
        message: 'Report checked successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to check report',
        color: 'red',
      });
    },
  });
};

export const useRejectCheck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: ApproveReportDto }) =>
      evaluationReportsApi.rejectCheck(reportId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReportApprovals'] });
      notifications.show({
        title: 'Report Rejected',
        message: 'Report has been rejected and sent back to generator',
        color: 'orange',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject report',
        color: 'red',
      });
    },
  });
};

export const useApproveReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: ApproveReportDto }) =>
      evaluationReportsApi.approveReport(reportId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReportApprovals'] });
      notifications.show({
        title: 'Success',
        message: 'Report approved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve report',
        color: 'red',
      });
    },
  });
};

export const useRejectApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: ApproveReportDto }) =>
      evaluationReportsApi.rejectApproval(reportId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReportApprovals'] });
      notifications.show({
        title: 'Report Rejected',
        message: 'Report has been rejected and sent back to generator',
        color: 'orange',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject report',
        color: 'red',
      });
    },
  });
};

export const useAddBidCommitteeMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: AddBidCommitteeMembersDto }) =>
      evaluationReportsApi.addBidCommitteeMembers(reportId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReportApprovals'] });
      notifications.show({
        title: 'Success',
        message: 'Bid committee members added successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to add bid committee members',
        color: 'red',
      });
    },
  });
};

export const useApproveByBidCommittee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: ApproveReportDto }) =>
      evaluationReportsApi.approveByBidCommittee(reportId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReportApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] }); // Also invalidate the combined pending approvals
      notifications.show({
        title: 'Success',
        message: 'Report approved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve report',
        color: 'red',
      });
    },
  });
};

export const useRejectByBidCommittee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: ApproveReportDto }) =>
      evaluationReportsApi.rejectByBidCommittee(reportId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationReport'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReportApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] }); // Also invalidate the combined pending approvals
      notifications.show({
        title: 'Report Rejected',
        message: 'Report has been rejected and sent back to generator',
        color: 'orange',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject report',
        color: 'red',
      });
    },
  });
};

export const usePendingReportApprovals = () => {
  return useQuery({
    queryKey: ['pendingReportApprovals'],
    queryFn: () => evaluationReportsApi.getPendingReportApprovals(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

